import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { challengeApi } from '../services/challengeApi';
import { usePlayer } from '../contexts/PlayerContext';
import { getGuestName, setGuestName } from '../utils/storage';

const getErrorMessage = (err: unknown): string =>
  err instanceof Error ? err.message : 'Something went wrong';

interface LeaderboardEntry {
  rank: number;
  name: string;
  guessCount: number;
  timeSeconds: number;
  isGuest: boolean;
}

interface LeaderboardProps {
  date?: string;
  mode?: string;
  length?: number;
}

export const Leaderboard = ({ date, mode, length }: LeaderboardProps) => {
  // A signed-in player is shown as `Name #TAG` (renamed on the Account page), so the guest-name form is not needed.
  const { profile } = usePlayer();
  const profileLabel = profile?.label;
  const [entries, setEntries] = useState<LeaderboardEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nameInput, setNameInput] = useState(() => getGuestName() ?? '');
  const [confirmedName, setConfirmedName] = useState(() => getGuestName());
  const [isSavingName, setIsSavingName] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);
  const [nameNotice, setNameNotice] = useState<string | null>(null);

  const fetchLeaderboard = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await challengeApi.getLeaderboard({
        date: date || new Date().toISOString().split('T')[0],
        mode: mode || 'daily',
        length: length || 5,
      });
      setEntries(data);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  }, [date, mode, length]);

  useEffect(() => {
    // Also refetch when the player's name changes, so their own row updates.
    fetchLeaderboard();
  }, [fetchLeaderboard, profileLabel]);

  const handleSaveName = async () => {
    const trimmed = nameInput.trim();
    if (!trimmed) {
      setNameError('Enter a name first');
      return;
    }

    setIsSavingName(true);
    setNameError(null);
    setNameNotice(null);
    try {
      const { guestName: savedName } = await challengeApi.updateGuestName(trimmed);
      setGuestName(savedName);
      setNameInput(savedName);
      setConfirmedName(savedName);
      if (savedName !== trimmed) {
        setNameNotice(`Saved as "${savedName}" — that name was already taken.`);
      }
      await fetchLeaderboard();
    } catch (err) {
      setNameError(getErrorMessage(err));
    } finally {
      setIsSavingName(false);
    }
  };

  return (
    <div className="space-y-4">
      {profileLabel || confirmedName ? (
        <div className="px-4 py-3 bg-[#222]/50 rounded-xl border border-white/5">
          <span className="text-xs text-white/50">Playing as </span>
          <span className="text-sm font-semibold text-white/90">{profileLabel ?? confirmedName}</span>
        </div>
      ) : (
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 px-4 py-3 bg-[#222]/50 rounded-xl border border-white/5">
          <label htmlFor="guest-name-input" className="text-xs text-white/50 whitespace-nowrap">
            Your leaderboard name
          </label>
          <div className="flex flex-1 gap-2">
            <input
              id="guest-name-input"
              type="text"
              maxLength={20}
              value={nameInput}
              onChange={(e) => {
                setNameInput(e.target.value);
                setNameNotice(null);
              }}
              placeholder="Guest Player"
              className="flex-1 bg-[#1a1a1a] border border-white/10 rounded-lg px-3 py-1.5 text-sm text-white/90 focus:outline-none focus:border-[#ed1c24]"
            />
            <button
              onClick={handleSaveName}
              disabled={isSavingName}
              className="px-4 py-1.5 text-xs font-semibold bg-[#ed1c24] rounded-lg disabled:opacity-50 transition-all"
            >
              {isSavingName ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      )}
      {nameError && <p className="px-4 text-xs text-red-400">{nameError}</p>}
      {nameNotice && <p className="px-4 text-xs text-white/40">{nameNotice}</p>}

      {isLoading ? (
        <div className="flex justify-center p-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#ed1c24]"></div>
        </div>
      ) : error ? (
        <div className="text-red-400 text-center p-6 bg-red-400/10 rounded-xl border border-red-400/20">
          <p>Could not load leaderboard: {error}</p>
        </div>
      ) : (
        <>
          <div className="flex justify-between items-center px-2 sm:px-4 py-2 text-xs font-medium text-white/40 uppercase tracking-widest">
            <div className="flex items-center gap-3 sm:gap-8">
              <span className="w-6 sm:w-8">Rank</span>
              <span>Player</span>
            </div>
            <div className="flex items-center gap-3 sm:gap-12">
              <span className="hidden sm:inline">Guesses</span>
              <span className="w-10 sm:w-16 text-right">Time</span>
            </div>
          </div>

          <div className="space-y-2">
            {entries.length === 0 ? (
              <div className="text-center p-12 text-white/30 italic">
                No verified results yet for this challenge.
              </div>
            ) : (
              entries.map((entry, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className={`flex justify-between items-center gap-2 p-3 sm:p-4 rounded-xl border transition-all duration-200 ${
                    entry.rank <= 3
                      ? 'bg-gradient-to-r from-[#5c1216]/60 to-transparent border-[#ed1c24]/30'
                      : 'bg-[#222]/50 border-white/5 hover:border-white/10'
                  }`}
                >
                  <div className="flex items-center gap-3 sm:gap-8 min-w-0">
                    <span className={`w-6 sm:w-8 shrink-0 font-bold text-lg ${
                      entry.rank === 1 ? 'text-yellow-400' :
                      entry.rank === 2 ? 'text-gray-300' :
                      entry.rank === 3 ? 'text-amber-600' : 'text-white/40'
                    }`}>
                      {entry.rank === 1 ? '🥇' : entry.rank === 2 ? '🥈' : entry.rank === 3 ? '🥉' : entry.rank}
                    </span>
                    <div className="flex flex-col min-w-0">
                      <span className="font-semibold text-white/90 truncate">
                        {entry.name}
                      </span>
                      {entry.isGuest && (
                        <span className="text-[10px] text-white/30 uppercase tracking-tighter">Verified Guest</span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-3 sm:gap-12 shrink-0">
                    <div className="hidden sm:flex gap-1">
                      {Array.from({ length: 6 }).map((_, i) => (
                        <div
                          key={i}
                          className={`w-2 h-2 rounded-full ${
                            i < entry.guessCount ? 'bg-[#ed1c24]' : 'bg-white/5'
                          }`}
                        />
                      ))}
                    </div>
                    <span className="sm:hidden text-xs font-mono text-white/40">
                      {entry.guessCount}/6
                    </span>
                    <span className="w-10 sm:w-16 text-right font-mono text-[#ff6b6f] text-xs sm:text-base">
                      {entry.timeSeconds}s
                    </span>
                  </div>
                </motion.div>
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
};
